import { ConversationV4 } from '@gfe/core/models/comments-v4.model';
import { EntityInterface } from '@gfe/core/models/entities/entity-visualization-interface';

/** An entity whose conversations land in an exported row's comments. */
export interface CommentSource {
  /** The comments API entity type, e.g. `ROUTE_ACTION`. */
  entityType: string;
  uid: string;
}

/** What the export needs of a row to find its comments: its uid and its detail entities. */
export type CommentableRow = Pick<EntityInterface<unknown>, 'uid' | 'getDetails'>;

/** The comments API keys entity types in upper case (`ROUTE_ACTION`); the front end in lower. */
export function commentEntityType(entityType: string): string {
  return entityType.toUpperCase();
}

export function conversationKey(entityType: string, uid: string): string {
  return `${entityType}\u0000${uid}`;
}

/**
 * The row itself (when `rowType` is commentable, i.e. non-null), then each detail
 * entity of every commentable `detailTypes` in the row's own order.
 */
export function commentSourcesOf(
  row: CommentableRow,
  rowType: string | null,
  detailTypes: string[]
): CommentSource[] {
  const sources: CommentSource[] = [];
  if (rowType !== null && row.uid) {
    sources.push({ entityType: rowType, uid: row.uid });
  }
  for (const detailType of detailTypes) {
    for (const detail of row.getDetails(detailType)) {
      if (detail.uid)
        sources.push({ entityType: commentEntityType(detailType), uid: detail.uid });
    }
  }
  return sources;
}

/** The distinct uids to fetch conversations for, grouped by entity type. */
export function commentUidsByType(sourcesPerRow: CommentSource[][]): Map<string, string[]> {
  const byType = new Map<string, Set<string>>();
  for (const source of sourcesPerRow.flat()) {
    const uids = byType.get(source.entityType) ?? new Set<string>();
    uids.add(source.uid);
    byType.set(source.entityType, uids);
  }
  return new Map([...byType].map(([type, uids]) => [type, [...uids]]));
}

export function indexConversations(
  conversations: ConversationV4[]
): Map<string, ConversationV4[]> {
  const byKey = new Map<string, ConversationV4[]>();
  for (const conversation of conversations) {
    const key = conversationKey(conversation.entityType, conversation.entityUid);
    const forKey = byKey.get(key);
    if (forKey === undefined) byKey.set(key, [conversation]);
    else forKey.push(conversation);
  }
  return byKey;
}

/** The field comments are exported under, kept clear of a row field already named `comments`. */
export function exportedCommentsKey(hasField: (field: string) => boolean): string {
  return hasField('comments') ? 'export_comments' : 'comments';
}

export interface ExportedComment {
  entity_type: string;
  entity_uid: string;
  resolved: boolean;
  author: string;
  created_at: string;
  message: string;
}

const byCreatedAt = (a: { createdAt: string }, b: { createdAt: string }) =>
  a.createdAt.localeCompare(b.createdAt);

/**
 * Every comment of the sources' conversations, source by source, each source's
 * conversations and each conversation's comments oldest first.
 */
export function exportedComments(
  sources: CommentSource[],
  conversations: Map<string, ConversationV4[]>
): ExportedComment[] {
  return sources.flatMap((source) =>
    [...(conversations.get(conversationKey(source.entityType, source.uid)) ?? [])]
      .sort(byCreatedAt)
      .flatMap((conversation) =>
        [...conversation.comments].sort(byCreatedAt).map((comment) => ({
          entity_type: source.entityType,
          entity_uid: source.uid,
          resolved: conversation.resolved,
          author: comment.createdBy.fullname,
          created_at: comment.createdAt,
          message: comment.message
        }))
      )
  );
}

/**
 * One line per comment, `[Type uid] (resolved) Author — date — message`. Only a comment
 * on a detail entity, i.e. not of the row's own `rowType`, names its entity, and only a
 * resolved conversation's comment carries the marker.
 */
export function commentsCellValue(
  comments: ExportedComment[],
  rowType: string,
  format: {
    date: (iso: string) => string;
    resolved: string;
    typeName: (entityType: string) => string;
  }
): string {
  return comments
    .map((comment) => {
      const prefix = [
        comment.entity_type === rowType
          ? ''
          : `[${format.typeName(comment.entity_type)} ${comment.entity_uid}]`,
        comment.resolved ? `(${format.resolved})` : ''
      ]
        .filter((part) => part !== '')
        .join(' ');
      const body = `${comment.author} — ${format.date(comment.created_at)} — ${comment.message}`;
      return prefix === '' ? body : `${prefix} ${body}`;
    })
    .join('\n');
}
